import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../components/button'
import { BackButton } from '../../components/button/backButton'
import { Status } from '../../components/cards/statusTag'
import Icon from '../../components/icon'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { ConfirmationModal } from '../../components/modal/app_modals/confirmationModal'
import { CreateEventModal } from '../../components/modal/app_modals/createEventModal'
import { EventGuestsCheckInModal } from '../../components/modal/app_modals/eventGuestsCheckInModal'
import { ExportDataModal } from '../../components/modal/app_modals/exportDataModal'
import Spinner from '../../components/spinner/Spinner'
import { TableComponent } from '../../components/table'
import { PageHeader } from '../../components/typography/PageHeader'
import { ticketStatusMap } from '../../constants'
import { formatCurrency, getEventPriceText } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import {
  useCheckInTicketMutation,
  useGetEventStatsQuery,
  useGetEventTicketsQuery,
} from '../../redux-toolkits/events/events.slice'
import {
  SingleTicketPurchase,
  TicketStatus,
} from '../../redux-toolkits/events/events.type'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
export const SingleEventPage = () => {
  const { id } = useParams()

  const [query, setQuery] = useState<
    QueryProps & {
      status?: TicketStatus
    }
  >({
    page: 1,
    limit: 30,
    search: undefined,
  })
  const [selectedTicket, setSelectedTicket] = useState<SingleTicketPurchase>()

  const navigate = useNavigate()

  const {
    data: eventStats,
    isFetching: fetcingEventStats,
    refetch: refetchEventStats,
  } = useGetEventStatsQuery(
    {
      id: id ?? '',
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    },
  )

  const {
    data: eventTickets,
    isFetching: fetcingEventTickets,
    refetch: refetchEventTickets,
  } = useGetEventTicketsQuery(
    {
      id: id ?? '',
      ...query,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    },
  )

  const [checkIn, { isLoading: checkingIn }] = useCheckInTicketMutation()

  const { handleModalClose, handleModalOpen, modal, modalOpen, setModalOpen } =
    useModalControl()

  return (
    <>
      <Layout>
        {fetcingEventStats ? (
          <div className="flex items-center justify-center">
            <Spinner fullScreen={false} />
          </div>
        ) : (
          <div className="flex flex-col">
            <BackButton />

            <div className="mt-10 flex flex-col md:flex-row flex-wrap w-full items-start gap-4 justify-between">
              <div className="flex flex-col gap-2">
                <PageHeader
                  title={`${eventStats?.data?.event?.name}`}
                  className="flex items-center gap-4 !my-0 md:w-fit w-full"
                />

                <p className="text-sec-black text-base font-medium">
                  {getEventPriceText(
                    eventStats?.data?.event?.price ?? 0,
                    eventStats?.data?.event?.eventTicketSettings?.tiers ?? [],
                  )}
                </p>

                <div className="flex items-center gap-2 text-sm">
                  <p>
                    <span className="font-medium">
                      {`${eventStats?.data?.event?.inStockQuantity?.toLocaleString()}`}
                    </span>{' '}
                    tickets
                  </p>
                  <p className="text-base">•</p>
                  <p>
                    <span className="font-medium">
                      {`${eventStats?.data?.stats?.totalTickets?.toLocaleString()}`}
                    </span>{' '}
                    units sold
                  </p>
                  <p className="text-base">•</p>
                  <Status
                    type={
                      eventStats?.data?.event?.isActive ? 'success' : 'fail'
                    }
                    text={
                      eventStats?.data?.event?.isActive ? 'Active' : 'Inactive'
                    }
                  />
                </div>
              </div>

              <div className="max-md:order-first max-md:ml-auto  flex flex-row gap-4 items-center flex-1 justify-end">
                <Dropdown
                  menuButton={
                    <Button
                      icon="ellipses"
                      className="size-[58px] !px-0"
                      type="button"
                    />
                  }
                  menuItems={[
                    {
                      name: `Edit`,
                      value: '',
                      onClick: () => {
                        handleModalOpen('editEventModal')
                      },
                    },
                    {
                      name: `Check-In Guests`,
                      value: '',
                      onClick: () => {
                        handleModalOpen('guestCheckInModal')
                      },
                    },
                  ]}
                  menuItemClassName="border-b-0"
                  menuClassName="!w-[200px]"
                />
              </div>
            </div>

            <div className="mt-10 flex items-center gap-4 flex-wrap justify-between">
              <div className="!w-full md:!max-w-[400px]">
                <SearchInput
                  searchFunction={(search) => {
                    setQuery({
                      ...query,
                      search: search || undefined,
                      page: 1,
                    })
                  }}
                  placeholder="Search"
                />
              </div>

              <Dropdown
                menuClassName=""
                menuButton={
                  <Button
                    label={`Status: ${
                      query.status ? ticketStatusMap[query.status].text : 'All'
                    }`}
                    primary={false}
                    icon="filter"
                    className="!font-normal ml-auto"
                  />
                }
                menuItems={[
                  {
                    name: 'All',
                    value: 'all',
                    onClick: () => {
                      setQuery({
                        ...query,
                        page: 1,
                        status: undefined,
                      })
                    },
                  },
                  ...Object.keys(ticketStatusMap).map((key) => ({
                    name: ticketStatusMap[key as TicketStatus].text,
                    value: key,
                    onClick: () => {
                      setQuery({
                        ...query,
                        page: 1,
                        status: key as TicketStatus,
                      })
                    },
                  })),
                ]}
                className="ml-auto"
              />

              <Button
                label="Export"
                primary
                onClick={() => {
                  handleModalOpen('exportDataModal')
                }}
              />
            </div>

            <div className="w-full mt-6 overflow-x-scroll">
              <TableComponent
                loading={fetcingEventTickets}
                headers={[
                  'Ticket Code',
                  'Customer Name',
                  'Customer Email',
                  'Price Paid',
                  'Status',
                  'Actions',
                ]}
                totalDataCount={eventTickets?.data?.pagination?.total}
                currentPage={Number(query.page ?? 0)}
                pageLimit={Number(query.limit ?? 0)}
                isEmpty={[undefined, 0].includes(
                  eventTickets?.data?.tickets?.length,
                )}
                onPageChange={(p) => {
                  setQuery({
                    ...query,
                    page: p,
                  })
                }}
                rows={
                  eventTickets?.data?.tickets?.map((ticket) => {
                    return {
                      id: ticket._id,
                      content: [
                        ticket.ticketCode,
                        <p
                          className="capitalize"
                          key={`${ticket._id}-customer-name`}
                        >
                          {ticket.customerName}
                        </p>,
                        ticket.customerEmail,
                        formatCurrency(ticket.pricePaid, {
                          currency: 'NGN',
                          decimalPlaces: 2,
                        }),
                        <Status
                          text={ticketStatusMap[ticket.status].text}
                          type={ticketStatusMap[ticket.status].pillVariant}
                          key={`${ticket._id}-status`}
                        />,
                        <Dropdown
                          className="text-left"
                          key={`${ticket._id}-controls`}
                          menuButton={
                            <Icon
                              id="ellipses"
                              height={18}
                              width={18}
                              className=""
                            />
                          }
                          menuItems={[
                            {
                              name: 'Check In',
                              value: 'checkIn',
                              onClick: () => {
                                setSelectedTicket(ticket)
                                handleModalOpen('checkInModal')
                              },
                              hidden: ticket.status !== 'ACTIVE',
                            },
                          ]}
                        />,
                      ],
                    }
                  }) ?? []
                }
                emptyText="No Tickets Sold"
              />
            </div>
          </div>
        )}
      </Layout>

      <CreateEventModal
        modalOpen={modalOpen && modal === 'editEventModal'}
        handleModalClose={() => {
          handleModalClose()
        }}
        setModalOpen={setModalOpen}
        mode={'edit'}
        onSucess={() => {
          handleModalClose()
          refetchEventStats()
        }}
        event={eventStats?.data?.event}
      />

      {selectedTicket ? (
        <ConfirmationModal
          open={modalOpen && modal === 'checkInModal'}
          setOpen={setModalOpen}
          message={'Are you sure you want to proceed?'}
          title={'Confirm Check-In'}
          onCloseCallback={() => {
            handleModalClose()
          }}
          onConfirm={async () => {
            const resp = await checkIn({
              eventId: id ?? '',
              ticketCode: selectedTicket.ticketCode,
            })

            if (resp.data?.success) {
              handleModalClose()
              setSelectedTicket(undefined)
            }
          }}
          loading={checkingIn}
          confirmText="Check-In"
        />
      ) : undefined}

      <EventGuestsCheckInModal
        open={modalOpen && modal === 'guestCheckInModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
          refetchEventStats()
          refetchEventTickets()
        }}
        onSuccessCallback={() => {}}
        eventId={id ?? ''}
      />

      <ExportDataModal
        open={modalOpen && modal === 'exportDataModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
        extraValues={{
          eventId: id,
        }}
        title="Export Event Tickets"
        reportType="event_tickets"
      />
    </>
  )
}
